/**
 * Utilitaires pour les commandes de dates dynamiques.
 * Déclenchées par "//" dans les éditeurs de texte.
 *
 * Exemples : //Mois-1, //Année+2, //Date, //Trimestre, //Semaine, //Jour+1
 */

// ─── DEV ONLY ──────────────────────────────────────────────────────────────
// Pour simuler une date différente, remplacez null par ex: new Date('2026-06-15')
// Remettre null avant de committer / déployer.
const TEST_DATE = null;
function getNow() {
  return TEST_DATE ? new Date(TEST_DATE) : new Date();
}
// ───────────────────────────────────────────────────────────────────────────

const MONTH_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

const DAY_FR = [
  'dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi',
];

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function formatFullDate(date) {
  const day = date.getDate();
  const month = MONTH_FR[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

function nLabel(base, n) {
  if (n === 0) return base;
  if (n > 0) return `${base}+${n}`;
  return `${base}${n}`; // négatif inclut le "-" naturellement
}

/**
 * Génère tous les items de commandes de date disponibles.
 * @returns {Array} Liste d'items {kind, command, value, description, category, icon}
 */
export function generateDateCommands() {
  const now = getNow();
  const items = [];

  // ─── JOUR (nom du jour de la semaine) ────────────────────────────────────
  for (let n = -3; n <= 3; n++) {
    const d = addDays(now, n);
    const value = DAY_FR[d.getDay()];
    const desc =
      n === 0 ? "Aujourd'hui"
      : n === 1 ? 'Demain'
      : n === -1 ? 'Hier'
      : n > 0 ? `Dans ${n} jours`
      : `Il y a ${Math.abs(n)} jours`;
    items.push({
      kind: 'date',
      command: nLabel('Jour', n),
      value,
      description: desc,
      category: 'Jour',
      icon: '📅',
    });
  }

  // ─── MOIS (nom du mois) ──────────────────────────────────────────────────
  for (let n = -6; n <= 6; n++) {
    const d = addMonths(now, n);
    const value = MONTH_FR[d.getMonth()];
    const desc =
      n === 0 ? 'Ce mois-ci'
      : n > 0 ? `Dans ${n} mois`
      : `Il y a ${Math.abs(n)} mois`;
    items.push({
      kind: 'date',
      command: nLabel('Mois', n),
      value,
      description: desc,
      category: 'Mois',
      icon: '📆',
    });
  }

  // ─── MOISANNEE (ex: "mars 2026") ─────────────────────────────────────────
  for (let n = -6; n <= 6; n++) {
    const d = addMonths(now, n);
    const value = `${MONTH_FR[d.getMonth()]} ${d.getFullYear()}`;
    const desc =
      n === 0 ? 'Ce mois-ci (avec année)'
      : n > 0 ? `Dans ${n} mois`
      : `Il y a ${Math.abs(n)} mois`;
    items.push({
      kind: 'date',
      command: nLabel('MoisAn', n),
      value,
      description: desc,
      category: 'MoisAn',
      icon: '🗓️',
    });
  }

  // ─── ANNEE ───────────────────────────────────────────────────────────────
  for (let n = -5; n <= 5; n++) {
    const year = now.getFullYear() + n;
    const desc =
      n === 0 ? 'Cette année'
      : n > 0 ? `Dans ${n} an(s)`
      : `Il y a ${Math.abs(n)} an(s)`;
    items.push({
      kind: 'date',
      command: nLabel('Année', n),
      value: String(year),
      description: desc,
      category: 'Année',
      icon: '🗓️',
    });
    // Alias anglais
    items.push({
      kind: 'date',
      command: nLabel('Year', n),
      value: String(year),
      description: desc,
      category: 'Année',
      icon: '🗓️',
    });
  }

  // ─── DATE COMPLETE (ex: "2 mars 2026") ───────────────────────────────────
  const dateOffsets = [-7, -3, -2, -1, 0, 1, 2, 3, 7, 14, 30];
  for (const n of dateOffsets) {
    const d = addDays(now, n);
    const value = formatFullDate(d);
    const desc =
      n === 0 ? "Aujourd'hui"
      : n === 1 ? 'Demain'
      : n === -1 ? 'Hier'
      : n > 0 ? `Dans ${n} jours`
      : `Il y a ${Math.abs(n)} jours`;
    items.push({
      kind: 'date',
      command: nLabel('Date', n),
      value,
      description: desc,
      category: 'Date',
      icon: '📅',
    });
  }

  // ─── SEMAINE ─────────────────────────────────────────────────────────────
  const weekNum = getWeekNumber(now);
  items.push({
    kind: 'date',
    command: 'Semaine',
    value: `S${weekNum}`,
    description: `Semaine ${weekNum} de l'année`,
    category: 'Semaine',
    icon: '📅',
  });
  items.push({
    kind: 'date',
    command: 'SemaineNum',
    value: String(weekNum),
    description: `Numéro de la semaine (${weekNum})`,
    category: 'Semaine',
    icon: '📅',
  });
  // Semaine prochaine / précédente
  items.push({
    kind: 'date',
    command: 'Semaine+1',
    value: `S${weekNum + 1}`,
    description: 'Semaine prochaine',
    category: 'Semaine',
    icon: '📅',
  });
  items.push({
    kind: 'date',
    command: 'Semaine-1',
    value: `S${weekNum - 1}`,
    description: 'Semaine dernière',
    category: 'Semaine',
    icon: '📅',
  });

  // ─── TRIMESTRE ───────────────────────────────────────────────────────────
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  items.push({
    kind: 'date',
    command: 'Trimestre',
    value: `T${quarter} ${now.getFullYear()}`,
    description: 'Trimestre actuel',
    category: 'Trimestre',
    icon: '📊',
  });
  items.push({
    kind: 'date',
    command: 'TrimestreNum',
    value: `T${quarter}`,
    description: `Numéro du trimestre (T${quarter})`,
    category: 'Trimestre',
    icon: '📊',
  });
  for (let n = -3; n <= 3; n++) {
    if (n === 0) continue;
    const d = addMonths(now, n * 3);
    const q = Math.ceil((d.getMonth() + 1) / 3);
    const desc =
      n > 0 ? `Dans ${n} trimestre(s)`
      : `Il y a ${Math.abs(n)} trimestre(s)`;
    items.push({
      kind: 'date',
      command: nLabel('Trimestre', n),
      value: `T${q} ${d.getFullYear()}`,
      description: desc,
      category: 'Trimestre',
      icon: '📊',
    });
  }

  // ─── ALIAS ANGLAIS (Month, Day) ───────────────────────────────────────────
  for (let n = -6; n <= 6; n++) {
    const d = addMonths(now, n);
    const value = MONTH_FR[d.getMonth()];
    const desc =
      n === 0 ? 'Ce mois-ci'
      : n > 0 ? `Dans ${n} mois`
      : `Il y a ${Math.abs(n)} mois`;
    items.push({
      kind: 'date',
      command: nLabel('Month', n),
      value,
      description: desc,
      category: 'Mois',
      icon: '📆',
    });
  }
  for (let n = -3; n <= 3; n++) {
    const d = addDays(now, n);
    const value = DAY_FR[d.getDay()];
    const desc =
      n === 0 ? "Aujourd'hui"
      : n === 1 ? 'Demain'
      : n === -1 ? 'Hier'
      : n > 0 ? `Dans ${n} jours`
      : `Il y a ${Math.abs(n)} jours`;
    items.push({
      kind: 'date',
      command: nLabel('Day', n),
      value,
      description: desc,
      category: 'Jour',
      icon: '📅',
    });
  }

  return items;
}

/**
 * Tente de résoudre une commande de date arbitraire (hors des plages prédéfinies).
 * Gère des patterns comme "Mois+7", "Année-10", "Date+45", "Jour+10", etc.
 * @param {string} query - Commande tapée après "//"
 * @returns {Object|null} Item de commande ou null si non reconnu
 */
function tryResolveArbitraryCommand(query) {
  if (!query || !query.trim()) return null;

  const q = query.trim();
  // Extraire la base et l'offset optionnel, ex: "Mois+7" → base="Mois", offset=7
  const match = q.match(/^([A-Za-zÀ-ÿ]+)([+-]\d+)?$/);
  if (!match) return null;

  const base = match[1];
  const offsetStr = match[2];
  const n = offsetStr ? parseInt(offsetStr, 10) : 0;

  const now = getNow();

  // Mois / Month
  if (/^(Mois|Month)$/i.test(base)) {
    const d = addMonths(now, n);
    const value = MONTH_FR[d.getMonth()];
    const desc = n === 0 ? 'Ce mois-ci' : n > 0 ? `Dans ${n} mois` : `Il y a ${Math.abs(n)} mois`;
    return { kind: 'date', command: q, value, description: desc, category: 'Mois', icon: '📆' };
  }

  // MoisAn
  if (/^MoisAn$/i.test(base)) {
    const d = addMonths(now, n);
    const value = `${MONTH_FR[d.getMonth()]} ${d.getFullYear()}`;
    const desc = n === 0 ? 'Ce mois-ci (avec année)' : n > 0 ? `Dans ${n} mois` : `Il y a ${Math.abs(n)} mois`;
    return { kind: 'date', command: q, value, description: desc, category: 'MoisAn', icon: '🗓️' };
  }

  // Année / Year / Annee
  if (/^(Ann[eé]e|Year)$/i.test(base)) {
    const year = now.getFullYear() + n;
    const desc = n === 0 ? 'Cette année' : n > 0 ? `Dans ${n} an(s)` : `Il y a ${Math.abs(n)} an(s)`;
    return { kind: 'date', command: q, value: String(year), description: desc, category: 'Année', icon: '🗓️' };
  }

  // Jour / Day
  if (/^(Jour|Day)$/i.test(base)) {
    const d = addDays(now, n);
    const value = DAY_FR[d.getDay()];
    const desc = n === 0 ? "Aujourd'hui" : n === 1 ? 'Demain' : n === -1 ? 'Hier' : n > 0 ? `Dans ${n} jours` : `Il y a ${Math.abs(n)} jours`;
    return { kind: 'date', command: q, value, description: desc, category: 'Jour', icon: '📅' };
  }

  // Date
  if (/^Date$/i.test(base)) {
    const d = addDays(now, n);
    const value = formatFullDate(d);
    const desc = n === 0 ? "Aujourd'hui" : n === 1 ? 'Demain' : n === -1 ? 'Hier' : n > 0 ? `Dans ${n} jours` : `Il y a ${Math.abs(n)} jours`;
    return { kind: 'date', command: q, value, description: desc, category: 'Date', icon: '📅' };
  }

  // Trimestre
  if (/^Trimestre$/i.test(base)) {
    const d = addMonths(now, n * 3);
    const q2 = Math.ceil((d.getMonth() + 1) / 3);
    const value = `T${q2} ${d.getFullYear()}`;
    const desc = n === 0 ? 'Trimestre actuel' : n > 0 ? `Dans ${n} trimestre(s)` : `Il y a ${Math.abs(n)} trimestre(s)`;
    return { kind: 'date', command: q, value, description: desc, category: 'Trimestre', icon: '📊' };
  }

  // Semaine
  if (/^Semaine$/i.test(base)) {
    const d = addDays(now, n * 7);
    const wNum = getWeekNumber(d);
    const value = `S${wNum}`;
    const desc = n === 0 ? `Semaine ${wNum} de l'année` : n > 0 ? `Dans ${n} semaine(s)` : `Il y a ${Math.abs(n)} semaine(s)`;
    return { kind: 'date', command: q, value, description: desc, category: 'Semaine', icon: '📅' };
  }

  // SemaineNum
  if (/^SemaineNum$/i.test(base)) {
    const d = addDays(now, n * 7);
    const wNum = getWeekNumber(d);
    const desc = n === 0 ? `Numéro de la semaine (${wNum})` : n > 0 ? `Dans ${n} semaine(s)` : `Il y a ${Math.abs(n)} semaine(s)`;
    return { kind: 'date', command: q, value: String(wNum), description: desc, category: 'Semaine', icon: '📅' };
  }

  return null;
}

/**
 * Filtre les commandes de date selon la requête tapée après "//".
 * @param {string} query - Texte tapé après "//"
 * @returns {Array} Items filtrés (max 30)
 */
export function filterDateCommands(query) {
  const all = generateDateCommands();
  if (!query || query.trim() === '') {
    // Sans filtre, afficher un sous-ensemble représentatif
    const representatives = ['Date', 'Mois', 'Mois-1', 'Mois+1', 'MoisAn', 'Année', 'Année-1', 'Année+1', 'Jour', 'Jour+1', 'Semaine', 'Trimestre'];
    const rep = all.filter(item => representatives.includes(item.command));
    // Compléter avec d'autres si besoin
    const extra = all.filter(item => !representatives.includes(item.command)).slice(0, 8);
    return [...rep, ...extra].slice(0, 25);
  }

  const q = query.toLowerCase();
  const trimmed = query.trim();

  // Toujours tenter la résolution arbitraire : si le pattern est valide (ex: Mois+12)
  // on propose l'item custom en tête, même si des prédéfinis partiels existent aussi
  const custom = tryResolveArbitraryCommand(trimmed);

  // Items prédéfinis qui matchent partiellement la requête
  const filtered = all.filter(item =>
    item.command.toLowerCase().includes(q) ||
    item.value.toLowerCase().includes(q) ||
    item.category.toLowerCase().includes(q)
  );

  if (custom) {
    // Vérifier si la commande custom est identique à un item prédéfini déjà présent
    const duplicateInFiltered = filtered.some(
      item => item.command.toLowerCase() === custom.command.toLowerCase()
    );
    if (!duplicateInFiltered) {
      // Mettre le custom en tête (commande exacte résolue) + les partiels en dessous
      return [custom, ...filtered].slice(0, 26);
    }
    // La commande est dans les prédéfinis : la remonter en tête pour la mettre en évidence
    const reordered = [
      ...filtered.filter(item => item.command.toLowerCase() === custom.command.toLowerCase()),
      ...filtered.filter(item => item.command.toLowerCase() !== custom.command.toLowerCase()),
    ];
    return reordered.slice(0, 25);
  }

  return filtered.slice(0, 25);
}

/**
 * Résout une commande de date (sans les "//") et retourne la valeur de texte.
 * @param {string} command - ex: "Mois-1", "Année+2"
 * @returns {string|null} Valeur résolue ou null si commande inconnue
 */
export function resolveDateCommand(command) {
  const all = generateDateCommands();
  const found = all.find(item => item.command.toLowerCase() === command.toLowerCase());
  if (found) return found.value;
  // Fallback : tenter la résolution arbitraire (ex: Mois+7, Année-10)
  const custom = tryResolveArbitraryCommand(command);
  return custom ? custom.value : null;
}

/**
 * Remplace toutes les balises {{date:Commande}} dans un contenu HTML
 * par leur valeur résolue *au moment de l'appel* (résolution dynamique côté affichage).
 *
 * Exemple : {{date:Mois-1}} → "février" si on est en mars
 *
 * @param {string} html - Le contenu HTML brut
 * @returns {string} Le HTML avec les balises remplacées
 */
export function resolveDateTags(html) {
  if (!html || !html.includes('{{date:')) return html;
  return html.replace(/\{\{date:([^}]+)\}\}/g, (match, command) => {
    const value = resolveDateCommand(command.trim());
    if (value == null) return match; // commande inconnue : laisser tel quel
    return `<span class="date-tag" title="//\u200b${command.trim()}" style="font-weight:600">${value}</span>`;
  });
}
